import React from 'react'
import { useParamsInt } from '../../../../../../shared/hooks/location'
import { useAuditRecord } from '../../../../../../shared/audit/history/audit-record-context'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useApiFormAssetVersion } from '../../../../../../api-new/state/content'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { CardFullHeightScrollY } from '../../../../../../shared/card/card-full-height-scroll-y'
import { FormSimple } from '../../../../../../shared/form-simple/form'
import { FormSimpleDateFormat } from '../../../../../../shared/form-simple/form-data-format'
import { FormSimpleText } from '../../../../../../shared/form-simple/form-text'
import { FormSimpleNumber } from '../../../../../../shared/form-simple/form-number'
import { FormSimpleTextarea } from '../../../../../../shared/form-simple/form-textarea'
import { ButtonSubmit } from '../../../../../../shared/form-simple/form-submit'
import { Menu, MenuDelete, MenuItem } from '../../../../../../shared/menu/menu'
import { IconCheck, IconDownload, IconEdit } from '../../../../../../shared/icon/icon'
import { useDownloadPresign } from '../../../../../../shared/download/hook'
import { useRoleSysApp } from '../../../../../../role/hook/role-sys'
import { FormSimpleJsonEditor } from '../../../../../../shared/form-simple/form-json-editor'
import { FormSimpleSelectBool } from '../../../../../../shared/form-simple/form-select-bool'
import { Label } from '../../../../../../shared/form-simple/common/label'

// Large transfer logs (hundreds of events, chunk arrays, etc.) choke the
// inline JSON editor and can freeze the browser tab. Render a download
// button instead so admins can grab the raw JSON locally and open it in
// whatever editor they prefer. Filename mirrors the format Caffeine uses
// for client-side log files (yyyy-MM-dd_HHmmss-Upload-vN-<shortId>.json)
// so engineers can cross-reference server-stored vs locally-persisted logs.
const padZero = n => String(n).padStart(2, '0')

const parseTransferLog = value => {
  if (!value) return null
  if (typeof value !== 'string') return value
  try { return JSON.parse(value) } catch { return null }
}

const buildTransferLogFilename = (version, log) => {
  const ts = new Date(version?.updatedAt || version?.createdAt || Date.now())
  const stamp = `${ts.getFullYear()}-${padZero(ts.getMonth() + 1)}-${padZero(ts.getDate())}_${padZero(ts.getHours())}${padZero(ts.getMinutes())}${padZero(ts.getSeconds())}`
  const xferId = log && typeof log.TransferId === 'string' ? log.TransferId.slice(0, 8) : 'log'
  const vid = version?.id ? `v${version.id}` : 'v0'
  return `${stamp}-Upload-${vid}-${xferId}.json`
}

const downloadTransferLogAsFile = version => {
  const raw = version?.transferLog
  if (!raw) return
  const parsed = parseTransferLog(raw)
  const contents = parsed ? JSON.stringify(parsed, null, 2) : String(raw)
  const filename = buildTransferLogFilename(version, parsed)
  const blob = new Blob([contents], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Small delay before revoking so the browser has a moment to start the save.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const TransferLogDownloadRow = ({ version }) => {
  const hasLog = !!version?.transferLog
  return (
    <Label name="transferLog" label="transfer log">
      {hasLog ? (
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => downloadTransferLogAsFile(version)}
        >
          <IconDownload /> <span className="ms-1">download transfer log (.json)</span>
        </button>
      ) : (
        <div className="text-muted fst-italic">no transfer log recorded</div>
      )}
    </Label>
  )
}

export const AssetVersionEdit = ({ parentURL }) => {
  const { assetVersionID } = useParamsInt()
  // this version's History ("Version N"), not the asset's
  useAuditRecord({ type: 'asset_versions', id: assetVersionID, role: { org: 'NODE' } })

  const history = useHistory()

  const apiFormAssetVersion = useApiFormAssetVersion()

  const roleSysApp = useRoleSysApp()

  const { active, download } = useDownloadPresign()

  const handlePath = () => apiFormAssetVersion.get(`${assetVersionID}`)

  const handleSetCurrent = async id => {
    await apiFormAssetVersion.put(`${id}/current`)
    await apiFormAssetVersion.get(`${assetVersionID}`)
  }

  const handleDownload = async id => {
    let presigned = await apiFormAssetVersion.get(`${id}/presigned-download`)
    download(presigned.url)
  }

  const handleDelete = async id => {
    await apiFormAssetVersion.delete(id)
    history.replace(parentURL)
  }

  const handleSubmit = async data => {
    await apiFormAssetVersion.put(data.id, data)
  }

  const HeaderMenu = () =>
    <Menu>
      <MenuItem
        Icon={IconCheck}
        text="set actuve"
        onClick={() => handleSetCurrent(assetVersionID)}
        show={roleSysApp.accessUpdate}
        disabled={apiFormAssetVersion.form.status !== 'complete'}
      />
      <MenuItem Icon={IconDownload} text="download" onClick={() => handleDownload(assetVersionID)} show={roleSysApp.accessRead} disabled={active} />
      <MenuDelete onClick={() => handleDelete(assetVersionID)} show={roleSysApp.accessDelete} />
    </Menu>

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconEdit} text={apiFormAssetVersion.form.filename} onPath={handlePath}>
        <CardFullHeightScrollY title={apiFormAssetVersion.form.filename} HeaderMenu={HeaderMenu}>
          <FormSimple state={apiFormAssetVersion.form} onSubmit={handleSubmit} onRefresh={handlePath}>
            <FormSimpleDateFormat label="created" name="createdAt" />
            <FormSimpleDateFormat label="updated" name="updatedAt" />
            <FormSimpleNumber label="versionCount" name="versionCount" readonly />
            <FormSimpleText label="storageKey" name="storageKey" readonly />
            <FormSimpleNumber label="content length" name="contentLength" readonly />
            <FormSimpleText label="content sha256" name="contentSha256" readonly />
            <FormSimpleSelectBool label="isCurrent" name="isCurrent" readonly />
            <FormSimpleText label="uploaded by" name="uploadedBy" readonly />
            <FormSimpleText label="filename" name="filename" />
            <FormSimpleText name="status" readonly />
            <FormSimpleTextarea label="notes" name="note" />
            <FormSimpleJsonEditor name="metadata" />
            <TransferLogDownloadRow version={apiFormAssetVersion.form} />
            <ButtonSubmit text="save version" />
          </FormSimple>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
